import React from 'react';

import { DefaultSpinner, NoData } from 'components';
import LocationScopeGate from 'hooks/useAdminGroup/LocationScopeGate';
import { useScopedApiFilters } from 'hooks/useAdminGroup/useScopedApiFilters';
import type { ChartFilters } from 'hooks/useAdminGroup/applyLocationScope';

/**
 * The slice of the analytics module state ScopedSection reads and rewrites.
 * The sections receive the full state object back with only apiFilters
 * replaced, so everything else passes through untouched.
 */
export interface AnalyticsSectionState {
  apiFilters: ChartFilters;
  filterOptions?: unknown;
  [key: string]: unknown;
}

export interface AnalyticsSectionProps {
  state: AnalyticsSectionState;
  actions?: Record<string, unknown>;
}

export interface ScopedSectionProps {
  component: React.ComponentType<AnalyticsSectionProps>;
  state: AnalyticsSectionState;
  actions?: Record<string, unknown>;
}

// The Analytics tab does not render through ReportPageChart: its four
// sections (trend, comparison, period, call-duration) pass the Redux
// controls `state.apiFilters` straight into AnalyticsChart, MultiPieContainer
// and the panel/table fetches. This wrapper is the tab's single scope
// boundary -- the admin-group/location breadcrumb scope is applied to
// `state.apiFilters` here, before any section renders, so every widget below
// fetches scoped. Held (gate) while an admin-group scope's subtree is still
// resolving, so sections never briefly fetch account-wide data.
//
// The scope is sent under `filters.scope_locations` -- NOT merged into
// `filters.locations` the way ReportPageChart does for Reporting/Quality.
// The analytics endpoints infer the report bucket from which entity filter
// keys are present (`locations` wins over users/call_groups/teams), the
// custom-group (teams) member expansion ORs entity filters together, and the
// comparison panels rebuild `filters` from scratch per comparator. Merging
// the scope into `locations` would flip comparison/panel bucketing on every
// non-Location view and OR-widen custom-group reports; `scope_locations` is
// consumed by NextosReportBase._resolve_locations and ANDed unconditionally.
// The Location view's own `locations` bucket filter still intersects with
// the scope client-side, and its degraded seed (/nextos/locations returned
// no options) is recovered from the breadcrumb hierarchy -- the full
// decision table lives in scopedApiFilters.js.
// useScopedApiFilters memoizes the derived apiFilters on the values the
// decision table actually reads, so the object identity only changes when
// the scope or the underlying Redux filters change -- section widgets (the
// call-log table's componentDidUpdate in particular) hang refetch
// comparisons off that reference.
const ScopedSection = ({
  component: Component,
  state,
  actions,
}: ScopedSectionProps) => {
  const { scope, apiFilters, hold, isEmptyIntersection } = useScopedApiFilters(
    state.apiFilters,
    state.filterOptions,
  );

  return (
    <LocationScopeGate scope={scope}>
      {hold ? (
        <DefaultSpinner />
      ) : isEmptyIntersection ? (
        <NoData />
      ) : (
        <Component state={{ ...state, apiFilters }} actions={actions} />
      )}
    </LocationScopeGate>
  );
};

export default ScopedSection;
